import { useState, useEffect, useRef } from 'react';
import { Download, RotateCw, FlipHorizontal, FlipVertical, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { loadImage, readFileAsDataURL } from '@/lib/utils';
import { processAndSave, canvasToBlob, stripExt } from '@/lib/bulk';

interface RotateOpts {
  angle: number;
  flipX: boolean;
  flipY: boolean;
}

function applyRotation(canvas: HTMLCanvasElement, img: HTMLImageElement, o: RotateOpts): void {
  const rad = (o.angle * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  canvas.width = Math.ceil(img.width * cos + img.height * sin);
  canvas.height = Math.ceil(img.width * sin + img.height * cos);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rad);
  ctx.scale(o.flipX ? -1 : 1, o.flipY ? -1 : 1);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);
}

export function ImageRotate() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [previewImg, setPreviewImg] = useState<HTMLImageElement | null>(null);
  const [angle, setAngle] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFiles([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(incoming: File[]) {
    if (!incoming.length) return;
    const next = [...files, ...incoming.filter((f) => f.type.startsWith('image/'))];
    setFiles(next);
    if (!previewImg && next[0]) {
      const url = await readFileAsDataURL(next[0]);
      setPreviewImg(await loadImage(url));
    }
  }

  function removeFile(i: number) {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0) {
      setPreviewImg(null);
    } else if (i === 0) {
      readFileAsDataURL(next[0]).then(loadImage).then(setPreviewImg);
    }
  }

  useEffect(() => {
    if (!previewImg || !canvasRef.current) return;
    applyRotation(canvasRef.current, previewImg, { angle, flipX, flipY });
  }, [previewImg, angle, flipX, flipY]);

  async function exportAll() {
    if (files.length === 0) return;
    setWorking(true);
    setProgress({ done: 0, total: files.length });
    try {
      const opts: RotateOpts = { angle, flipX, flipY };
      await processAndSave(
        files,
        async (file) => {
          const url = await readFileAsDataURL(file);
          const im = await loadImage(url);
          const c = document.createElement('canvas');
          applyRotation(c, im, opts);
          const blob = await canvasToBlob(c, 'image/png');
          return { blob, filename: `${stripExt(file.name)}-rotated.png` };
        },
        {
          zipName: 'rotated',
          onProgress: (done, total) => setProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="image-rotate"
        title="Rotate & flip"
        description="Rotate by any angle and flip horizontally/vertically. Drop multiple files for bulk."
        badges={files.length > 1 ? <Badge variant="accent">{files.length} files</Badge> : null}
      />

      {files.length === 0 ? (
        <Dropzone accept="image/*" multiple onFiles={handleFiles} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-3">
            <div className="grid grid-cols-4 gap-1.5">
              {[0, 90, 180, 270].map((a) => (
                <button
                  key={a}
                  onClick={() => setAngle(a)}
                  className={`px-2 py-1.5 text-xs rounded-md border transition ${
                    angle === a
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border hover:border-border-strong'
                  }`}
                >
                  {a}°
                </button>
              ))}
            </div>
            <Field label="Custom angle" hint={`${angle}°`}>
              <Slider value={angle} onChange={setAngle} min={-180} max={180} showValue={false} />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={flipX ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFlipX((x) => !x)}
              >
                <FlipHorizontal size={12} />
                Flip H
              </Button>
              <Button
                variant={flipY ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFlipY((y) => !y)}
              >
                <FlipVertical size={12} />
                Flip V
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFiles([]);
                  setPreviewImg(null);
                }}
              >
                Clear
              </Button>
              <Button variant="primary" size="sm" loading={working} onClick={exportAll}>
                <Download size={11} />
                {files.length > 1 ? `Export ${files.length}` : 'Export'}
              </Button>
            </div>
            {working && progress.total > 1 && (
              <div className="text-2xs text-fg-subtle">
                Processing {progress.done} / {progress.total}…
              </div>
            )}
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Preview {files.length > 1 && <span className="text-fg-subtle">· first of {files.length}</span>}
            </div>
            <div className="p-6 bg-bg-inset min-h-[360px] flex items-center justify-center">
              <canvas ref={canvasRef} className="max-w-full max-h-[420px] rounded shadow-card" />
            </div>
            {files.length > 1 && (
              <div className="border-t border-border px-3 py-2">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-2xs rounded-md bg-bg-inset border border-border max-w-[200px]"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        className="shrink-0 size-4 rounded hover:bg-surface-hover text-fg-subtle hover:text-danger flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-1 px-2 py-0.5 text-2xs rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 cursor-pointer">
                    + add more
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || !list.length) return;
                        handleFiles(Array.from(list));
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default ImageRotate;
